import { motion } from "framer-motion";
import { Check, Mail, Phone, ShieldCheck, Truck } from "lucide-react";
import { SectionTransition } from "@/components/SectionTransition";

const EASE = [0.16, 1, 0.3, 1];

const REQUIREMENTS = [
  "Active operating authority in good standing",
  "Current cargo and liability insurance",
  "Completed W-9 and carrier documentation",
  "Safe operating history and roadworthy equipment",
  "Dependable pickup, transit, and delivery communication",
];

const EQUIPMENT = [
  "Dry van and refrigerated",
  "Flatbed and stepdeck",
  "Power-only and specialized",
  "Box truck and hotshot",
];

const CONTACTS = [
  {
    id: "primary",
    label: "PRIMARY DISPATCH",
    value: "+1 (805) 512-5109",
    href: "tel:+18055125109",
    icon: Phone,
  },
  {
    id: "backup",
    label: "BACKUP DISPATCH",
    value: "+1 (805) 415-5433",
    href: "tel:+18054155433",
    icon: Phone,
  },
  {
    id: "email",
    label: "EMAIL DISPATCH",
    value: "m.a.brokerage1@gmail.com",
    href: "mailto:m.a.brokerage1@gmail.com",
    icon: Mail,
  },
];

export default function CarrierRequirements() {
  return (
    <section
      id="carrier-requirements"
      data-testid="carrier-requirements-section"
      className="relative scroll-mt-20 overflow-hidden border-t border-[#222736] py-28"
    >
      <SectionTransition testId="network-carrier-transition-line" />
      <div className="grid-lines absolute inset-0 opacity-20" />
      <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="max-w-4xl"
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#F59E0B]">
            04 // CARRIER PARTNERSHIPS
          </p>
          <h2
            data-testid="carrier-requirements-heading"
            className="mt-4 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl"
          >
            Carrier Requirements & Direct Contact
          </h2>
          <p className="mt-5 max-w-3xl text-base leading-7 text-slate-300 lg:text-[17px] lg:leading-8">
            Owner-operators and fleets interested in working with Merchant Alliance Brokerage can review the basic requirements below, then contact dispatch directly to discuss equipment, operating regions, and preferred lanes.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            data-testid="carrier-requirements-panel"
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="border border-[#383F56] bg-[#12141C] p-6 sm:p-8"
          >
            <div className="flex items-center justify-between gap-5 border-b border-[#222736] pb-5">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-[#10B981]" />
                <h3 className="font-display text-xl font-bold uppercase text-white">
                  Have These Ready
                </h3>
              </div>
              <span className="font-mono text-[10px] tracking-[0.18em] text-[#10B981]">
                BASIC REVIEW
              </span>
            </div>
            <ul className="mt-6 space-y-4">
              {REQUIREMENTS.map((requirement, index) => (
                <li
                  key={requirement}
                  data-testid={`carrier-requirement-${index + 1}`}
                  className="grid grid-cols-[18px_1fr] gap-3 text-base leading-7 text-slate-200"
                >
                  <Check className="mt-1.5 h-4 w-4 text-[#FF5500]" />
                  <span>{requirement}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 border-t border-[#222736] pt-6">
              <div className="flex items-center gap-3">
                <Truck className="h-4 w-4 text-[#F59E0B]" />
                <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-400">
                  EQUIPMENT OF INTEREST
                </p>
              </div>
              <p className="mt-4 text-base leading-7 text-slate-300">
                {EQUIPMENT.join(" · ")}
              </p>
            </div>
          </motion.div>

          <motion.div
            data-testid="carrier-direct-contact-panel"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.08, ease: EASE }}
            className="border border-[#4C5F84] bg-[#151923] p-6 sm:p-8"
          >
            <p className="font-mono text-[10px] font-bold tracking-[0.22em] text-[#B9C9E6]">
              CONTACT DISPATCH DIRECTLY
            </p>
            <h3 className="mt-4 font-display text-2xl font-black uppercase text-white sm:text-3xl">
              Ready To Talk?
            </h3>
            <p className="mt-4 text-base leading-7 text-slate-300">
              Have your MC or USDOT number, equipment type, current location, and preferred operating lanes ready when you reach out.
            </p>

            <div className="mt-8 space-y-3">
              {CONTACTS.map(({ id, label, value, href, icon: Icon }) => (
                <a
                  key={id}
                  data-testid={`carrier-contact-${id}-link`}
                  href={href}
                  className="group flex min-h-16 items-center gap-4 border border-[#383F56] bg-[#0A0B0E] px-4 py-3 transition-[border-color,background-color] duration-300 hover:border-[#6E87B4] hover:bg-[#1A1D28]"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-[#4C5F84] text-[#B9C9E6]">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-mono text-[9px] font-bold tracking-[0.18em] text-slate-500">
                      {label}
                    </span>
                    <span className="mt-1 block break-all text-sm font-semibold text-slate-100 sm:text-base">
                      {value}
                    </span>
                  </span>
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}